import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import { Box, Typography, Avatar } from "@mui/material";
import { NotificationsActive } from "@mui/icons-material";
import SignalRService from "./SignalRService";
import { axiosPrivate } from "../axios/axios";
import { masterAPI } from "../Pages/Masters/dataConfig";
import { stripHtml } from "./stripHtml";
import { getNotificationColor } from "./notificationVisual";
import { getNotificationRoute } from "./notificationRoutes";

// No-UI component: keeps one SignalR connection alive app-wide instead of
// each page opening/closing its own connection.
const NotificationListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth.loginInfo.accessToken);
  const userId = useSelector((state) => state.auth.loginInfo.id);

  useEffect(() => {
    if (!accessToken || !userId) {
      SignalRService.stop();
      return;
    }

    let isMounted = true;

    const getCountLink = masterAPI["notificationLog"].getcount;
    axiosPrivate
      .get(`${getCountLink}`)
      .then((res) => {
        if (isMounted) {
          dispatch({ type: "setUnreadCount", payload: res.data.result?.count });
        }
      })
      .catch((e) => {
        console.log(e, "NotificationLog getcount error");
      });

    const setup = async () => {
      try {
        const connection = await SignalRService.start();
        if (!isMounted) return;

        if (connection.state === signalR.HubConnectionState.Connected) {
          console.log("SignalR connected, joining group", userId);
        }

        await connection.invoke("JoinGroup", userId);


        connection.on("SendNotification", (data) => {
          console.log("SendNotification received:", data);
          dispatch({ type: "addNotification", payload: data });

          const route = getNotificationRoute(data.notificationType, data.referenceId);
          toast(
            <Box
              onClick={() => route && navigate(route)}
              sx={{ display: "flex", gap: 1.2, cursor: route ? "pointer" : "default" }}
            >
              <Avatar sx={{ bgcolor: getNotificationColor(data.notificationType), width: 32, height: 32 }}>
                <NotificationsActive fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                  {data.subject || "New Notification"}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  {stripHtml(data.htmlContent)}
                </Typography>
              </Box>
            </Box>,
            { autoClose: 8000 },
          );
        });

        connection.onreconnected(() => {
          connection.invoke("JoinGroup", userId);
        });
      } catch (err) {
        console.error("SignalR connection failed:", err);
      }
    };

    setup();

    return () => {
      isMounted = false;
      const connection = SignalRService.getConnection();
      if (connection) {
        if (connection.state === signalR.HubConnectionState.Connected) {
          connection.invoke("LeaveGroup", userId).catch((e) => {
            console.log(e, "LeaveGroup error");
          });
        }
        connection.off("SendNotification");
      }
    };
  }, [accessToken, userId, dispatch]);

  return null;
};

export default NotificationListener;
